import { Box, useColorModeValue } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { forwardRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import ShopProfilePage from "../../pages/ShopProfilePage";

const MotionBox = motion(Box);

const EASE = [0.16, 1, 0.3, 1];
const TRAY_PAD = "12px"; // matches ExplorePage p={3}
const TRAY_RADIUS = "16px";

const shellVariants = {
  hidden: {
    transition: { staggerChildren: 0.04, staggerDirection: -1 },
  },
  visible: {
    transition: { staggerChildren: 0.02 },
  },
};

const backdropVariants = {
  hidden: { opacity: 0, transition: { duration: 0.15 } },
  visible: { opacity: 1, transition: { duration: 0.15 } },
};

const panelVariants = {
  hidden: { x: "100%", transition: { duration: 0.26, ease: EASE } },
  visible: { x: 0, transition: { duration: 0.3, ease: EASE } },
};

/**
 * Desktop-only shell: dimmed Explore stays underneath; profile slides in from the right.
 * Inset + radius match the map tray so the panel feels like part of the same chrome.
 */
const ShopProfileOverlay = forwardRef(function ShopProfileOverlay(_props, ref) {
  const navigate = useNavigate();
  // Must match App explore paddingTop (md) so the panel sits under the header.
  const headerOffset = "84px";
  const panelW = "460px";
  const panelBg = useColorModeValue("#f5ede0", "#1a1209");

  const close = () => navigate(-1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MotionBox
      ref={ref}
      position="fixed"
      top={headerOffset}
      left={0}
      right={0}
      bottom={0}
      zIndex="modal"
      pointerEvents="none"
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={shellVariants}
    >
      <MotionBox
        position="absolute"
        inset={0}
        bg="transparent"
        variants={backdropVariants}
        onClick={close}
        pointerEvents="auto"
        cursor="default"
        aria-hidden
      />
      {/* Slide wrapper — keep transform off the radius clip layer */}
      <MotionBox
        position="absolute"
        top={TRAY_PAD}
        right={TRAY_PAD}
        bottom={TRAY_PAD}
        w={panelW}
        maxW={`calc(100% - ${TRAY_PAD} * 2)`}
        variants={panelVariants}
        pointerEvents="auto"
        style={{ willChange: "transform" }}
      >
        <Box
          h="100%"
          bg={panelBg}
          borderRadius={TRAY_RADIUS}
          overflow="hidden"
          boxShadow="-16px 0 48px rgba(0,0,0,0.32)"
          role="dialog"
          aria-modal="true"
          aria-label="Shop profile"
          sx={{ transform: "translateZ(0)" }}
        >
          <ShopProfilePage asPanel />
        </Box>
      </MotionBox>
    </MotionBox>
  );
});

export default ShopProfileOverlay;
